// src/pages/Signup.js
import React, { useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link, useNavigate } from "react-router-dom";

export default function Signup() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accept, setAccept] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");

  const pwdStrength = useMemo(() => {
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s; // 0..4
  }, [password]);

  async function onSubmit(e) {
    e.preventDefault();
    setNote("");

    if (password !== confirm) return setNote("Passwords do not match.");
    if (!accept) return setNote("Please accept the Terms to continue.");

    setLoading(true);

    // 1) Create the auth user (also store username in user_metadata)
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username } },
    });

    if (error) {
      setLoading(false);
      return setNote(error.message);
    }

    // 2) If we already have a session (email confirmation OFF),
    //    update the profile row with the username now.
    //    If confirmations are ON, this step will run after first login instead.
    const userId = data.user?.id;
    if (userId) {
      const { error: upErr } = await supabase
        .from("profiles")
        .update({ username })
        .eq("id", userId);
      if (upErr) console.warn("Profile update error:", upErr.message);
    }

    setLoading(false);

    // 3) Navigate or prompt for email confirmation
    if (!data.session) {
      setNote("Check your inbox to confirm your email, then log in.");
    } else {
      nav("/dashboard");
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-10">
      {/* Background glow */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-60">
        <div className="absolute -top-32 left-[-40px] h-64 w-64 rounded-full bg-sky-500/25 blur-3xl" />
        <div className="absolute bottom-[-120px] right-[-60px] h-72 w-72 rounded-full bg-sky-400/15 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto flex max-w-5xl flex-col items-center justify-center gap-8 lg:flex-row">
        {/* Left blurb (hidden on small if you want minimal) */}
        <div className="max-w-md lg:w-1/2 hidden lg:block">
          <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-950/80 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-sky-100 shadow-[0_0_25px_rgba(56,189,248,0.5)]">
            <span className="h-1.5 w-1.5 rounded-full bg-sky-300" />
            Cpenn DFS — Sign up
          </div>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Create your account
          </h1>
          <p className="mt-3 text-sm text-slate-300 max-w-sm">
            Access projections, optimizers, and DFS tools across NFL, NBA, MLB, and NASCAR —
            all in one dashboard.
          </p>

          <ul className="mt-5 space-y-1.5 text-[11px] text-slate-300">
            <li className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] text-emerald-300">
                ✓
              </span>
              Single login for all Cpenn DFS tools.
            </li>
            <li className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] text-emerald-300">
                ✓
              </span>
              Built-in username support for leaderboards & Discord.
            </li>
          </ul>
        </div>

        {/* Form card */}
        <div className="w-full max-w-md rounded-2xl border border-slate-800/80 bg-slate-950/95 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.9)] backdrop-blur">
          <h1 className="text-xl font-bold text-slate-50 sm:text-2xl">
            Create account
          </h1>
          <p className="mt-1 text-xs text-slate-400 sm:text-sm">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-medium text-sky-400 hover:text-sky-300 underline-offset-2 hover:underline"
            >
              Log in
            </Link>
          </p>

          {note && (
            <div
              className={`mt-4 rounded-xl border px-3 py-2 text-xs sm:text-sm ${
                note.toLowerCase().includes("check your inbox") ||
                note.toLowerCase().includes("sent")
                  ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-100"
                  : "border-amber-400/60 bg-amber-500/10 text-amber-100"
              }`}
            >
              {note}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                Email
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 outline-none ring-sky-500 focus:border-sky-500 focus:ring-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                Username
              </label>
              <input
                placeholder="yourname"
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 outline-none ring-sky-500 focus:border-sky-500 focus:ring-1"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  placeholder="••••••••"
                  className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 pr-14 text-sm text-slate-100 placeholder:text-slate-500 outline-none ring-sky-500 focus:border-sky-500 focus:ring-1"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[11px] font-medium text-slate-400 hover:text-slate-200"
                >
                  {showPwd ? "Hide" : "Show"}
                </button>
              </div>

              {/* strength bar (logic unchanged) */}
              <div className="mt-2 h-1.5 w-full rounded-full bg-slate-800">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    pwdStrength <= 1
                      ? "bg-red-400 w-1/4"
                      : pwdStrength === 2
                      ? "bg-amber-400 w-2/4"
                      : pwdStrength === 3
                      ? "bg-sky-400 w-3/4"
                      : "bg-emerald-400 w-full"
                  }`}
                />
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                Use 8+ characters with a mix of letters, numbers &amp; symbols.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                Confirm password
              </label>
              <input
                type="password"
                placeholder="••••••••"
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 outline-none ring-sky-500 focus:border-sky-500 focus:ring-1"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
              />
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-300 sm:text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-500 bg-slate-900 text-sky-500 focus:ring-sky-500"
                checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
              />
              <span>
                I agree to the{" "}
                <a href="/terms" className="text-sky-400 hover:text-sky-300 underline underline-offset-2">
                  Terms
                </a>{" "}
                &amp;{" "}
                <a href="/privacy" className="text-sky-400 hover:text-sky-300 underline underline-offset-2">
                  Privacy
                </a>
                .
              </span>
            </label>

            <button
              disabled={loading}
              className="inline-flex w-full items-center justify-center rounded-lg bg-sky-500 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow hover:bg-sky-400 disabled:opacity-60"
              type="submit"
            >
              {loading ? "Creating..." : "Create account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
